/**
 * Per-school accent theming. The brand palette is CSS-variable driven (see
 * tailwind.config.js + index.css). Given a school's accent hex we generate a
 * full 50–900 ramp — anchored so the accent lands on brand-600, the primary
 * button shade — and set the variables on <html>. Passing null clears the
 * overrides so the SchoolOne-blue defaults from index.css take over again.
 */

const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
type Rgb = { r: number; g: number; b: number };

/** How far to mix the accent toward white (positive) or black (negative) for
 *  each shade. 600 is the accent itself. */
const MIX: Record<(typeof SHADES)[number], number> = {
  50: 0.93, 100: 0.86, 200: 0.74, 300: 0.58, 400: 0.36, 500: 0.16,
  600: 0, 700: -0.16, 800: -0.3, 900: -0.44,
};

function parseHex(hex: string): Rgb | null {
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
}

/** Mix toward white when amt>0, toward black when amt<0 (|amt| in 0..1). */
function mix(c: Rgb, amt: number): Rgb {
  const target = amt >= 0 ? 255 : 0;
  const t = Math.abs(amt);
  const ch = (v: number) => Math.round(v + (target - v) * t);
  return { r: ch(c.r), g: ch(c.g), b: ch(c.b) };
}

const rgbChannels = (c: Rgb) => `${c.r} ${c.g} ${c.b}`;

/** Whether a hex string is a usable accent colour. */
export function isValidAccent(hex: string | undefined | null): boolean {
  return !!hex && parseHex(hex) !== null;
}

/** Apply (or, with null/invalid, clear) a school's accent colour. */
export function applyBrandAccent(hex: string | null | undefined): void {
  const root = document.documentElement;
  const base = hex ? parseHex(hex) : null;
  if (!base) {
    for (const s of SHADES) root.style.removeProperty(`--brand-${s}`);
    return;
  }
  for (const s of SHADES) root.style.setProperty(`--brand-${s}`, rgbChannels(mix(base, MIX[s])));
}
